import React, { useMemo, useState } from 'react';
import { FiRefreshCw, FiTable } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  emptyDealModel,
  computeDerived,
  PAYMENT_FREQUENCIES,
} from '../../config/dealPacketForms';
import AmortizationModal from './AmortizationModal';

const money = (v) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => {
  const x = parseFloat(String(v).replace(/[$,]/g, ''));
  return Number.isFinite(x) ? x : 0;
};

const Row = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right';

const SummaryLine = ({ label, value, strong }) => (
  <div className={`flex justify-between py-1 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
    <span>{label}</span>
    <span>${money(value)}</span>
  </div>
);

/**
 * DeskManager-style Quick Deal desking screen: price/down/rate/term in, live
 * TX contract summary out, plus reverse calculation (pick the payment, solve
 * the cash down or the term). Uses the exact same math as the deal packets.
 */
const QuickDealCalculator = () => {
  const [model, setModel] = useState(() => {
    const m = emptyDealModel();
    m.dealType = 'bhph';
    m.money.numPayments = 24;
    m.money.apr = 14.9;
    return m;
  });
  const [showAmortization, setShowAmortization] = useState(false);
  const [targetPayment, setTargetPayment] = useState('');

  const setMoney = (k, v) =>
    setModel((p) => ({ ...p, money: { ...p.money, [k]: v } }));
  const setTrade = (k, v) =>
    setModel((p) => ({ ...p, trade: { ...p.trade, [k]: v } }));

  const derived = useMemo(() => computeDerived(model), [model]);
  const m = model.money;

  const freq =
    PAYMENT_FREQUENCIES.find((f) => f.value === m.paymentFrequency) || PAYMENT_FREQUENCIES[0];
  const r = num(m.apr) / 100 / freq.periodsPerYear;
  const nPay = Math.max(Math.round(num(m.numPayments)), 0);

  // Reverse calculation — closed-form, since cash down never changes TX tax
  const reverse = useMemo(() => {
    const target = num(targetPayment);
    if (target <= 0) return null;

    const annuityPV = r > 0 ? (1 - Math.pow(1 + r, -nPay)) / r : nPay;
    const financeableAtTarget = target * annuityPV;
    const beforeDown = derived.cashPriceWithTax + derived.otherCharges;
    const downNeeded =
      beforeDown - financeableAtTarget - derived.netTrade - derived.deferredTotal;

    let termNeeded = null;
    const af = derived.amountFinanced;
    if (af > 0) {
      if (r === 0) {
        termNeeded = Math.ceil(af / target);
      } else if (target > af * r) {
        termNeeded = Math.ceil(-Math.log(1 - (af * r) / target) / Math.log(1 + r));
      }
    }
    return { downNeeded: Math.max(downNeeded, 0), termNeeded };
  }, [targetPayment, r, nPay, derived]);

  const reset = () => {
    const fresh = emptyDealModel();
    fresh.dealType = 'bhph';
    fresh.money.numPayments = 24;
    fresh.money.apr = 14.9;
    setModel(fresh);
    setTargetPayment('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quick Deal Calculator</h1>
          <p className="text-sm text-gray-600">
            Desk a payment in seconds — same Texas tax &amp; fee math as the deal packets.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowAmortization(true)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <SafeIcon icon={FiTable} className="w-4 h-4" /> Amortization
          </button>
          <button
            type="button"
            onClick={reset}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Deal */}
        <div className="bg-white rounded-xl shadow p-5 space-y-3">
          <h2 className="font-semibold text-gray-900 border-b pb-2">Deal</h2>
          <Row label="Vehicle price">
            <input className={inputCls} type="number" value={m.salePrice || ''} placeholder="0.00"
              onChange={(e) => setMoney('salePrice', e.target.value)} />
          </Row>
          <Row label="Cash down">
            <input className={inputCls} type="number" value={m.cashDown || ''} placeholder="0.00"
              onChange={(e) => setMoney('cashDown', e.target.value)} />
          </Row>
          <Row label="Trade-in allowance">
            <input className={inputCls} type="number" value={model.trade.allowance || ''} placeholder="0.00"
              onChange={(e) => setModel((p) => ({
                ...p,
                trade: { ...p.trade, hasTrade: num(e.target.value) > 0, allowance: e.target.value },
              }))} />
          </Row>
          <Row label="Trade-in payoff">
            <input className={inputCls} type="number" value={model.trade.payoff || ''} placeholder="0.00"
              onChange={(e) => setTrade('payoff', e.target.value)} />
          </Row>
          <Row label="Accessories">
            <input className={inputCls} type="number" value={m.accessoriesTotal || ''} placeholder="0.00"
              onChange={(e) => setMoney('accessoriesTotal', e.target.value)} />
          </Row>
          <Row label="Doc fee">
            <input className={inputCls} type="number" value={m.docFee}
              onChange={(e) => setMoney('docFee', e.target.value)} />
          </Row>
          <div className="text-xs text-gray-500 pt-1">
            Title, registration, inspection, and county fees use the Paradise Performance
            defaults; tax is TX motor-vehicle {m.taxRate}%.
          </div>
        </div>

        {/* Financing */}
        <div className="bg-white rounded-xl shadow p-5 space-y-3">
          <h2 className="font-semibold text-gray-900 border-b pb-2">Financing</h2>
          <Row label="APR %">
            <input className={inputCls} type="number" step="0.1" value={m.apr}
              onChange={(e) => setMoney('apr', e.target.value)} />
          </Row>
          <Row label="Number of payments">
            <input className={inputCls} type="number" value={m.numPayments}
              onChange={(e) => setMoney('numPayments', e.target.value)} />
          </Row>
          <Row label="Payment frequency">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={m.paymentFrequency}
              onChange={(e) => setMoney('paymentFrequency', e.target.value)}
            >
              {PAYMENT_FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>{f.value}</option>
              ))}
            </select>
          </Row>
          <Row label="First payment date">
            <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              type="date" value={m.firstPaymentDate}
              onChange={(e) => setMoney('firstPaymentDate', e.target.value)} />
          </Row>

          {/* Reverse calculation */}
          <div className="border-t pt-3 mt-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Reverse calculation — set the payment
            </h3>
            <Row label={`Target payment (${m.paymentFrequency.toLowerCase()})`}>
              <input className={inputCls} type="number" value={targetPayment} placeholder="e.g. 250"
                onChange={(e) => setTargetPayment(e.target.value)} />
            </Row>
            {reverse && (
              <div className="mt-2 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                <p>
                  Keep {nPay} payments → needs <strong>${money(reverse.downNeeded)}</strong> cash down.
                </p>
                <p>
                  {reverse.termNeeded
                    ? <>Keep current down → needs a term of <strong>{reverse.termNeeded}</strong> payments.</>
                    : <>Current down can&apos;t reach that payment at {m.apr}% APR — the payment doesn&apos;t cover interest.</>}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Contract summary */}
        <div className="bg-primary-50 rounded-xl shadow p-5">
          <h2 className="font-semibold text-gray-900 border-b border-primary-200 pb-2 mb-2">
            Contract Summary
          </h2>
          <SummaryLine label={`Payment (${m.paymentFrequency.toLowerCase()})`} value={derived.paymentAmount} strong />
          <SummaryLine label="Amount financed" value={derived.amountFinanced} />
          <SummaryLine label="Finance charge" value={derived.financeCharge} />
          <SummaryLine label="Sales tax" value={derived.salesTax} />
          <SummaryLine label="Other charges & fees" value={derived.otherCharges} />
          <SummaryLine label="Total down payment" value={derived.totalDown} />
          <SummaryLine label="Net trade-in" value={derived.netTrade} />
          <SummaryLine label="Total of payments" value={derived.totalOfPayments} />
          <div className="border-t border-primary-200 mt-2 pt-2">
            <SummaryLine label="Total sale price (out the door)" value={derived.totalSalePrice} strong />
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Ready to write it up? Build the full deal in Deal Builder — this screen is for
            desking numbers only and saves nothing.
          </p>
        </div>
      </div>

      {showAmortization && (
        <AmortizationModal model={model} onClose={() => setShowAmortization(false)} />
      )}
    </div>
  );
};

export default QuickDealCalculator;
